// The autonomous goal monitor's derivation — "are we actually moving forward?"
//
// ## Why this is derived and not stored
//
// The obvious source for a goal-progress page is `docs/plans/**/status.yml`: it
// already carries `status`, `percent`, per-slice percentages and a `current_focus`
// paragraph. It is also, measurably, wrong. On 2026-08-17 four plans claimed
// `not_started | 0` while their work was live in prod — `101-versatile-vps-agent-team`
// (epic #1051 CLOSED, docs 02/04/08/13 shipped), `101-commercial-vision-v1` (Phase 2
// milestone 23/23, prod since 2026-06-30), `102-memory-gardener-runner-env-hygiene`
// (`apps/runner/src/hygiene/` ships), `101-session-replay-decision-diff` (#811/#819
// shipped) — and four more still carried the template's own placeholder comment as
// their value. It is a known defect (#601).
//
// A monitor whose entire question is "are we moving forward?" cannot be built on a
// number a human remembers to update: it would answer NO about work that shipped
// weeks ago, and it would answer it behind a progress bar that looks authoritative.
// So every field here is COMPUTED from evidence the platform already produces —
// issues opened and closed, which the bot maintains on merge without being asked.
//
// ## Why recency is a first-class field, not a nicety
//
// A percentage alone cannot answer the question. `101-platform-vision-arc` has
// claimed 91% for weeks. A goal at 91% that nothing has touched in a month and a
// goal at 60% closing something daily render as near-identical progress bars and
// are opposite answers. {@link GoalStatus.Stalled} is therefore derived from
// ACTIVITY, never set by hand — it is the one status nobody would ever remember to
// apply to their own work.

import { z } from 'zod';

/** How a goal is doing, in the words a human would use. */
export const GoalStatus = {
  /** Nothing scoped yet — no issues carry it. Distinct from `in_progress` at 0%. */
  NotStarted: 'not_started',
  /** Open work, and something closed recently. */
  InProgress: 'in_progress',
  /**
   * Open work, and NOTHING closed in {@link GOAL_STALLED_DAYS}. The status the
   * page exists to surface: it is the difference between a goal being worked and a
   * goal merely being open, and it is invisible on a progress bar.
   */
  Stalled: 'stalled',
  /**
   * Every remaining item is waiting on a human (`hitl` / `blocked`). Told apart
   * from `stalled` because the remedy is different: stalled needs attention,
   * blocked needs a decision, and reporting one as the other sends someone to fix
   * the wrong thing.
   */
  Blocked: 'blocked',
  /** Everything scoped is closed. */
  Finished: 'finished',
} as const;
export type GoalStatus = (typeof GoalStatus)[keyof typeof GoalStatus];

/** Human label per status — one place, so the API and the UI cannot word it differently. */
export const GOAL_STATUS_LABEL: Record<GoalStatus, string> = {
  [GoalStatus.NotStarted]: 'Not started',
  [GoalStatus.InProgress]: 'In progress',
  [GoalStatus.Stalled]: 'Stalled',
  [GoalStatus.Blocked]: 'Blocked',
  [GoalStatus.Finished]: 'Finished',
};

/**
 * Days without a closed item after which an unfinished goal reads STALLED.
 *
 * 14 rather than 7: this repo ships in bursts, and a week of silence is an
 * ordinary gap between two waves of one plan — calling that stalled would train
 * everyone to ignore the badge, which costs more than the badge is worth. Two
 * weeks of nothing closed on a goal with open work is a real question.
 */
export const GOAL_STALLED_DAYS = 14;

/** Where a goal came from. Both are live GitHub state, never a hand-kept file. */
export const GoalKind = {
  /** A GitHub milestone — the versioned roadmap (`v1.1`, `Phase 3 — BYOVM`). */
  Milestone: 'milestone',
  /** A `type/epic` issue — one idea, tracked as its own thread. */
  Epic: 'epic',
} as const;
export type GoalKind = (typeof GoalKind)[keyof typeof GoalKind];

/** What the derivation needs to know about one goal. Everything is observed, nothing asserted. */
export type GoalInput = {
  kind: GoalKind;
  /** Stable key — the milestone number or the epic's issue number. */
  id: string;
  title: string;
  url: string;
  /** Items still open under this goal. */
  open: number;
  /** Items closed under this goal. */
  closed: number;
  /**
   * Open items waiting on a human (`hitl` / `blocked` labels).
   *
   * Structurally a subset of `open`, but NOT enforced here, because for a
   * milestone the two arrive from different reads: `open` is GitHub's own
   * counter, `blocked` is counted off the bounded issue scan. A close landing
   * between those two calls can leave `blocked` momentarily the larger. That
   * costs one row a count that is high by one until the next read — which is
   * why this is a note and not a `throw`: validating it would trade a cosmetic
   * blip on one row for a 503 that blanks the whole board.
   */
  blocked: number;
  /** When something under this goal last closed. Null when nothing ever has. */
  lastClosedAt: Date | null;
  /** The milestone's due date, when it has one. */
  dueOn: Date | null;
};

/** One goal, as the monitor reports it. */
export const GoalSchema = z.object({
  kind: z.enum(GoalKind),
  id: z.string().min(1),
  title: z.string().min(1),
  url: z.string().url(),
  status: z.enum(GoalStatus),
  /** 0-100, floored — see {@link deriveGoal} for why it never rounds UP to 100. */
  percent: z.number().int().min(0).max(100),
  /**
   * Does a PERCENTAGE mean anything for this goal?
   *
   * False for a goal made of ONE item, which is every `type/epic` issue: an epic
   * is a whole body of work tracked as a single thread, so `0/1 = 0%` renders a
   * large in-flight effort as untouched — the same flattering-number defect as
   * rounding 199/200 up to 100, pointed the other way. A consumer shows a bar
   * only when this is true; the rest get status and age, which are true of them.
   */
  measurable: z.boolean(),
  open: z.number().int().nonnegative(),
  closed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  /**
   * Days since anything closed here; null when nothing ever has — and null on a
   * FINISHED goal, where the number answers nothing (a goal completed in June is
   * not "stale", it is done) and only invites a reader to treat age as decay.
   */
  daysSinceProgress: z.number().int().nonnegative().nullable(),
  lastClosedAt: z.string().datetime().nullable(),
  dueOn: z.string().datetime().nullable(),
  /** Past its due date with work still open. */
  overdue: z.boolean(),
});
export type Goal = z.infer<typeof GoalSchema>;

/** Whole-milliseconds days between two instants, floored and never negative. */
function daysBetween(from: Date, to: Date): number {
  return Math.max(0, Math.floor((to.getTime() - from.getTime()) / 86_400_000));
}

/**
 * Grade one goal. Pure — `now` is injected, so the same inputs always answer the
 * same thing and a test needs no clock.
 *
 * Order matters and encodes the priority a reader needs:
 *
 * 1. **Nothing scoped** is `not_started`, never `in_progress` at 0%. A goal nobody
 *    has broken into issues yet is a planning gap; a goal with ten open issues and
 *    none closed is an execution one, and they need different conversations.
 * 2. **Everything closed** is `finished`, and requires `open === 0` — not merely
 *    99.5% rounding up. A goal reported finished with one item open is exactly the
 *    lie this page exists to remove.
 * 3. **Every remaining item blocked** outranks stalled: a goal waiting on a human
 *    decision has not stalled, it has been handed over. Reporting it as stalled
 *    sends someone to chase work nobody can do.
 * 4. **No progress in {@link GOAL_STALLED_DAYS}** is `stalled` — including a goal
 *    that has NEVER closed anything but has open work, since "opened three weeks
 *    ago, nothing closed" is the same fact.
 * 5. Otherwise `in_progress`.
 */
export function deriveGoal(input: GoalInput, now: Date): Goal {
  const total = input.open + input.closed;
  // FLOOR, never round: 199 of 200 closed is 99%, not "100% and one item open".
  // A goal is only ever reported at 100 by the `finished` branch below.
  const percent = total === 0 ? 0 : Math.floor((input.closed / total) * 100);
  const daysSinceProgress =
    input.lastClosedAt === null ? null : daysBetween(input.lastClosedAt, now);

  const status: GoalStatus =
    total === 0
      ? GoalStatus.NotStarted
      : input.open === 0
        ? GoalStatus.Finished
        : input.blocked >= input.open
          ? GoalStatus.Blocked
          : daysSinceProgress === null || daysSinceProgress >= GOAL_STALLED_DAYS
            ? GoalStatus.Stalled
            : GoalStatus.InProgress;

  return GoalSchema.parse({
    kind: input.kind,
    id: input.id,
    title: input.title,
    url: input.url,
    status,
    percent: status === GoalStatus.Finished ? 100 : percent,
    // A one-item goal is binary; a percentage over it is decoration.
    measurable: total > 1,
    open: input.open,
    closed: input.closed,
    total,
    blocked: input.blocked,
    daysSinceProgress: status === GoalStatus.Finished ? null : daysSinceProgress,
    lastClosedAt: input.lastClosedAt?.toISOString() ?? null,
    dueOn: input.dueOn?.toISOString() ?? null,
    // Overdue only means something while work is OPEN — a goal finished after its
    // due date is finished, and flagging it red forever would be noise.
    overdue: input.dueOn !== null && input.open > 0 && input.dueOn.getTime() < now.getTime(),
  });
}

/** The roll-up the page leads with. */
export const GoalSummarySchema = z.object({
  goals: z.number().int().nonnegative(),
  finished: z.number().int().nonnegative(),
  inProgress: z.number().int().nonnegative(),
  stalled: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  notStarted: z.number().int().nonnegative(),
  /** Open items across every goal — the size of what is left, in one number. */
  openItems: z.number().int().nonnegative(),
  /**
   * Goals that cannot move without a human — `blocked` plus `stalled`.
   *
   * THE number this page leads with, ahead of the percentage. Measured here on
   * 2026-08-17: 91% complete and `inProgress: 0` — every unfinished goal was
   * waiting on a person. A progress bar showed 91% and looked healthy; this
   * number is the one that changes what somebody does next.
   */
  waitingOnYou: z.number().int().nonnegative(),
  /**
   * Percent across ALL items of MEASURABLE goals, not the mean of per-goal
   * percentages. A goal with three issues and one with three hundred are not
   * equal halves of "how far along are we", and averaging the percentages says
   * they are. One-item goals are excluded for the same reason they carry no bar:
   * fifteen closed epics would otherwise inflate the roadmap's number by
   * fifteen items of "progress" that were never scheduled work.
   */
  percent: z.number().int().min(0).max(100),
});
export type GoalSummary = z.infer<typeof GoalSummarySchema>;

/** Roll a graded set up. Pure. */
export function summarizeGoals(goals: readonly Goal[]): GoalSummary {
  const count = (s: GoalStatus) => goals.filter((g) => g.status === s).length;
  const measurable = goals.filter((g) => g.measurable);
  const closed = measurable.reduce((n, g) => n + g.closed, 0);
  const total = measurable.reduce((n, g) => n + g.total, 0);
  return GoalSummarySchema.parse({
    goals: goals.length,
    finished: count(GoalStatus.Finished),
    inProgress: count(GoalStatus.InProgress),
    stalled: count(GoalStatus.Stalled),
    blocked: count(GoalStatus.Blocked),
    notStarted: count(GoalStatus.NotStarted),
    openItems: goals.reduce((n, g) => n + g.open, 0),
    waitingOnYou: count(GoalStatus.Blocked) + count(GoalStatus.Stalled),
    percent: total === 0 ? 0 : Math.floor((closed / total) * 100),
  });
}

/**
 * Sort for reading, not for tidiness: what needs a human first.
 *
 * Stalled and blocked lead because they are the page's whole reason to exist —
 * a finished goal is a result, an in-progress one is fine, and those two are
 * questions. Within a band, the goal that has been quiet longest comes first.
 */
const STATUS_ORDER: Record<GoalStatus, number> = {
  [GoalStatus.Stalled]: 0,
  [GoalStatus.Blocked]: 1,
  [GoalStatus.InProgress]: 2,
  [GoalStatus.NotStarted]: 3,
  [GoalStatus.Finished]: 4,
};

export function sortGoalsForReview(goals: readonly Goal[]): Goal[] {
  return [...goals].sort((a, b) => {
    const band = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    if (band !== 0) return band;
    const quiet =
      (b.daysSinceProgress ?? Number.MAX_SAFE_INTEGER) -
      (a.daysSinceProgress ?? Number.MAX_SAFE_INTEGER);
    if (quiet !== 0) return quiet;
    return a.title.localeCompare(b.title);
  });
}
